"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { consentState } from "@/components/analytics/Analytics";
import { setConsent, useConsent } from "@/lib/useConsent";

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Denied consent stops GA4 and the Pixel writing cookies but leaves the ones an earlier
 * yes set. Expire them on every domain level they could have been set on (GA4 picks the
 * top-level domain), so saying no after a yes really leaves the visitor cookie-free.
 */
const clearTrackingCookies = () => {
  const labels = window.location.hostname.split(".");
  const domains = ["", ...labels.map((_, i) => `;domain=${labels.slice(i).join(".")}`)];
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0].trim();
    if (!/^(_ga|_gid|_fbp|_fbc)/.test(name)) continue;
    for (const domain of domains) document.cookie = `${name}=;max-age=0;path=/${domain}`;
  }
};

/**
 * Asks before any analytics cookie is set. The site's own bar, not a bolted-on grey one: white
 * ground, hairline top rule, the same buttons as every other CTA. It sits at the
 * bottom and blocks nothing, so a visitor can read the whole page and decide whenever.
 */
export const ConsentBanner = () => {
  const consent = useConsent();

  const decline = () => {
    // GA4 just switches to denied. The Meta Pixel cannot be told to stop, so changing the
    // answer from yes to no mid-visit reloads to get rid of it rather than merely unmount it.
    const pixelLoaded = Boolean(window.fbq);
    setConsent("denied");
    // Told here as well as by <Analytics />, whose update only lands after a re-render:
    // a hit in between would write the cookies straight back.
    window.gtag?.("consent", "update", consentState("denied"));
    clearTrackingCookies();
    if (pixelLoaded) window.location.reload();
  };

  return (
    <AnimatePresence>
      {consent === null && (
        <motion.div
          role="region"
          aria-label="Cookie choices"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.5, ease: EASE }}
          className="fixed inset-x-0 bottom-0 z-[45] border-t border-black/10 bg-white"
        >
          <div className="mx-auto flex max-w-[95vw] flex-col gap-4 px-4 py-4 sm:max-w-[90vw] sm:px-6 md:max-w-[1400px] md:flex-row md:items-center md:justify-between md:gap-8 md:px-8">
            <p className="text-sm leading-relaxed text-brand-ink/70">
              We use analytics cookies to see how people find the studio. Decline and everything
              here works exactly the same.
            </p>
            <div className="flex shrink-0 items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={decline} className="text-[13px]">
                Decline
              </Button>
              <Button type="button" size="sm" onClick={() => setConsent("granted")} className="text-[13px]">
                Accept
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
